import React, { useEffect, useState } from 'react';
import type { Organization, GlobalAsset, LocalIncidentManagementResult } from '@types';
import { useGlobalAssets } from '../context/GlobalAssetContext';
import { ICONS } from './constants';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { HelpSidebar } from './HelpSidebar';

interface LocalIncidentManagementBuilderProps {
    isOpen: boolean;
    onClose: () => void;
    organization: Organization;
    onComplete: (result: LocalIncidentManagementResult) => void;
    onRequestHelp?: (preferChat?: boolean) => void;
    initialIncidentType?: string;
    initialChecklist?: string;
}

export const LocalIncidentManagementBuilder: React.FC<LocalIncidentManagementBuilderProps> = ({ isOpen, onClose, organization, onComplete, onRequestHelp, initialIncidentType, initialChecklist }) => {
    const { assets } = useGlobalAssets();
    const [step, setStep] = useState(1);
    
    // Form State
    const [incidentType, setIncidentType] = useState(initialIncidentType || 'Medical Emergency');
    const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
    const [selectedRoles, setSelectedRoles] = useState<string[]>(['Site Manager']);
    
    // Engine State
    const [isGenerating, setIsGenerating] = useState(false);
    const [progress, setProgress] = useState(0);
    const [loadingText, setLoadingText] = useState('');

    const INCIDENT_TYPES = [
        'Medical Emergency',
        'Fire / Evacuation',
        'Active Threat / Security Incident',
        'Suspicious Package / Bomb Threat',
        'Hazardous Material Spill',
        'Severe Weather Event',
        'System / Power Failure',
        'Missing Person'
    ];

    const AVAILABLE_ROLES = [
        'Site Manager',
        'Security Guard',
        'First Aid / CPR Certified',
        'Floor Warden',
        'IT / Facility Maintenance',
        'HR / Communications Lead'
    ];

    useEffect(() => {
        if (!isOpen)
            return;

        setStep(1);
        setIncidentType(initialIncidentType || 'Medical Emergency');
    }, [isOpen, initialIncidentType, initialChecklist]);

    const toggleRole = (role: string) => {
        setSelectedRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
    };

    const handleGenerate = async () => {
        if (!selectedAssetId && assets.length > 0) return;
        
        setIsGenerating(true);
        setStep(4); // Loading step
        
        try {
            setLoadingText('Initializing Local Incident Command protocols...');
            setProgress(15);
            await new Promise(r => setTimeout(r, 1200));

            setLoadingText('Configuring Tactical Checklists...');
            setProgress(45);
            
            const selectedAssetData = assets.find(a => a.id === selectedAssetId);

            const functions = getFunctions();
            const generateLocalPlan = httpsCallable(functions, 'generateLocalIncidentPlan');
            const response = await generateLocalPlan({
                organizationId: organization?.id || 'default_org',
                incidentType,
                requestedChecklist: initialChecklist || null,
                location: selectedAssetData || { name: 'Generic Location', location: { address: 'Unknown' } },
                roles: selectedRoles
            });
            const result = response.data as LocalIncidentManagementResult;
            
            setProgress(85);
            setLoadingText('Drafting Communication Matrices...');
            await new Promise(r => setTimeout(r, 1000));
            
            setProgress(100);
            setTimeout(() => {
                onComplete(result);
            }, 800);

        } catch (error) {
            console.error("Local Incident Plan Generation Error", error);
            setLoadingText('ERROR: AI Generation Failed. Please try again.');
        } finally {
            setIsGenerating(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center animate-fade-in p-4">
            <div className={`absolute inset-0 bg-slate-900/90 backdrop-blur-xl ${!isGenerating ? 'cursor-pointer' : ''}`} onClick={!isGenerating ? onClose : undefined} />
            
            <div className="relative w-full max-w-4xl glass-panel p-8 flex flex-col transform transition-transform animate-scale-in text-left shadow-[0_0_100px_rgba(59,130,246,0.3)] border border-blue-500/50 rounded-2xl max-h-[90vh] overflow-y-auto custom-scrollbar">
                
                {/* Header */}
                <div className="flex justify-between items-start mb-8 border-b border-slate-700/50 pb-6">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400">
                                {ICONS.clipboard}
                            </div>
                            <h2 className="text-2xl font-black text-white tracking-widest uppercase">
                                Local Incident Management
                            </h2>
                        </div>
                        <p className="text-sm text-slate-400 font-mono tracking-widest pl-12 uppercase">
                            Generate immediate tactical action plans for localized events.
                        </p>
                    </div>
                    {!isGenerating && (
                        <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 transition-colors">
                            {ICONS.menuClose}
                        </button>
                    )}
                </div>

                {step === 1 && (
                    <div className="space-y-8 animate-fade-in">
                        {initialChecklist && (
                            <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-4">
                                <p className="text-xs font-bold uppercase tracking-widest text-emerald-300">Recommended Checklist</p>
                                <p className="mt-1 text-sm font-semibold text-emerald-100">{initialChecklist}</p>
                            </div>
                        )}
                        <div className="space-y-4">
                            <label className="block text-sm font-bold text-slate-300 uppercase tracking-widest">1. Select Incident Type</label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {INCIDENT_TYPES.map(s => (
                                    <button
                                        key={s}
                                        onClick={() => setIncidentType(s)}
                                        className={`p-4 rounded-lg border text-left cursor-pointer transition-all flex items-center justify-between ${incidentType === s ? 'bg-blue-600/20 border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.2)] text-blue-100' : 'bg-slate-800/50 border-slate-700 hover:border-slate-500 text-slate-400'}`}
                                    >
                                        <span className="text-sm font-bold leading-tight">{s}</span>
                                        {incidentType === s && (
                                            <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-end pt-6 border-t border-slate-800">
                            <button 
                                onClick={() => setStep(2)}
                                className="px-8 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded shadow-lg shadow-blue-500/20 transition-all uppercase tracking-widest"
                            >
                                Next: Target Facility
                            </button>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="space-y-6 animate-fade-in">
                        <div className="space-y-4">
                            <label className="block text-sm font-bold text-slate-300 uppercase tracking-widest">2. Select Impacted Facility</label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-64 overflow-y-auto custom-scrollbar p-2">
                                {assets.length === 0 && (
                                    <div className="col-span-2 text-center py-8 text-slate-500 font-mono text-sm border border-slate-800 border-dashed rounded-lg bg-slate-900/30">
                                        No assets registered globally. You can still generate a generic plan without a specific location.
                                    </div>
                                )}
                                {assets.map(asset => (
                                    <div 
                                        key={asset.id} 
                                        onClick={() => setSelectedAssetId(asset.id)}
                                        className={`p-4 rounded-lg border cursor-pointer transition-all flex items-start gap-3 ${selectedAssetId === asset.id ? 'bg-blue-600/20 border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.2)]' : 'bg-slate-800/50 border-slate-700 hover:border-slate-500'}`}
                                    >
                                        <div>
                                            <h4 className="font-bold text-slate-200 text-sm leading-tight">{asset.name}</h4>
                                            <p className="text-xs text-slate-400 mt-1 uppercase tracking-wider">{asset.type} • {asset.location?.address || 'Unknown Location'}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-between pt-6 border-t border-slate-800">
                            <button 
                                onClick={() => setStep(1)}
                                className="px-6 py-3 border border-slate-600 hover:border-slate-500 text-slate-300 font-bold rounded transition-all uppercase tracking-widest"
                            >
                                Back
                            </button>
                            <button 
                                onClick={() => setStep(3)}
                                disabled={!selectedAssetId && assets.length > 0}
                                className="px-8 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded shadow-lg shadow-blue-500/20 transition-all uppercase tracking-widest disabled:opacity-50 disabled:cursor-not-allowed flex justify-between"
                            >
                                Next: Team Roles
                            </button>
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="space-y-6 animate-fade-in">
                        <div className="space-y-4">
                            <label className="block text-sm font-bold text-slate-300 uppercase tracking-widest">3. Select Available On-Site Roles</label>
                            <p className="text-xs text-slate-500">The Insights engine will draft specific, actionable task lists for these responding roles.</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {AVAILABLE_ROLES.map(r => (
                                    <div 
                                        key={r} 
                                        onClick={() => toggleRole(r)}
                                        className={`p-4 rounded-lg border cursor-pointer transition-all flex items-center gap-3 ${selectedRoles.includes(r) ? 'bg-blue-600/20 border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.2)] text-blue-100' : 'bg-slate-800/50 border-slate-700 hover:border-slate-500 text-slate-400'}`}
                                    >
                                        <input 
                                            type="checkbox" 
                                            checked={selectedRoles.includes(r)} 
                                            onChange={() => {}} 
                                            className="flex-shrink-0"
                                        />
                                        <span className="text-sm font-bold leading-tight">{r}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-between pt-6 border-t border-slate-800">
                            <button 
                                onClick={() => setStep(2)}
                                className="px-6 py-3 border border-slate-600 hover:border-slate-500 text-slate-300 font-bold rounded transition-all uppercase tracking-widest"
                            >
                                Back
                            </button>
                            <button 
                                onClick={handleGenerate}
                                className="px-8 py-3 bg-red-600 hover:bg-red-500 text-white font-bold rounded shadow-lg shadow-red-500/20 transition-all uppercase tracking-widest flex items-center gap-2"
                            >
                                {ICONS.shield}
                                Generate Tactical Plan
                            </button>
                        </div>
                    </div>
                )}

                {step === 4 && (
                    <div className="py-20 flex flex-col items-center justify-center animate-fade-in text-center">
                        <div className="mb-8 relative">
                            <div className="absolute inset-0 bg-blue-500/20 blur-[50px] rounded-full animate-pulse" />
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" className="w-24 h-24 text-blue-500 relative z-10 animate-[spin_4s_linear_infinite]">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                            </svg>
                        </div>
                        
                        <p className="text-blue-400 font-mono text-sm tracking-widest mb-10 h-6">
                            {loadingText}
                        </p>

                        <div className="w-full max-w-lg bg-slate-800 rounded-full h-2 mb-4 overflow-hidden border border-slate-700">
                            <div 
                                className="bg-gradient-to-r from-blue-600 to-indigo-400 h-2 rounded-full transition-all duration-500 ease-out relative" 
                                style={{ width: `${progress}%` }}
                            >
                                <div className="absolute top-0 right-0 bottom-0 w-20 bg-white/30 blur-[4px]" />
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Help Sidebar Integration */}
                <div className="hidden lg:block">
                    <HelpSidebar 
                        title="Local Response Support"
                        description="Identify immediate tactical requirements for on-site emergencies."
                        instructions={[
                            "Specify the nature of the incident.",
                            "Link the event to a registered facility.",
                            "Audit available responding personnel.",
                            "Compile the immediate tactical action plan."
                        ]}
                        onRequestHelp={() => onRequestHelp && onRequestHelp(true)}
                        onContactConsultant={() => {}} 
                        theme="dark"
                    />
                </div>
            </div>
        </div>
    );
};
